import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { ShieldCheck } from 'lucide-react'
import { supabase } from '../../utils/supabase'
import { L } from '../ui/LavDropdown'
import { SvIc } from '../services/svBits'

/**
 * AdminManager — the "Admin" tab, shown only to merchants whose row has
 * is_admin = true (migration 39). It is just a merchant picker: choosing a
 * merchant hands it to MerchantDashboard, which then runs the WHOLE
 * dashboard (sidebar, top bar, notifications and every tab) for that
 * merchant — "act as merchant" mode, `?as=<merchant id>` in the URL.
 *
 * Nothing here bypasses RLS; access comes from the permissive admin
 * policies (migrations 39–42).
 */

const pillSx = {
	flex: 'none', display: 'inline-flex', alignItems: 'center', height: 21, px: '8px',
	borderRadius: '99px', bgcolor: L.surface, color: '#6E6886', fontSize: '11.5px', fontWeight: 750,
}

const avatarSx = {
	width: 34, height: 34, borderRadius: '10px', flex: 'none',
	bgcolor: L.primarySoft, color: L.primaryDeep, display: 'flex',
	alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 800,
}

const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }

export default function AdminManager({ merchant, onSelectMerchant }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const [merchants, setMerchants] = useState([])
	const [loading, setLoading] = useState(true)
	const [errorMessage, setErrorMessage] = useState('')
	const [query, setQuery] = useState('')

	useEffect(() => {
		let alive = true
		const load = async () => {
			setLoading(true)
			setErrorMessage('')
			try {
				const { data, error } = await supabase
					.from('merchant')
					.select('id, name, slug, email, active, created_at')
					.order('name', { ascending: true })
					.order('created_at', { ascending: true })
				if (error) throw error
				if (!alive) return
				setMerchants(data || [])
			} catch (e) {
				if (!alive) return
				setErrorMessage(e.message || 'Failed to load merchants')
			} finally {
				if (alive) setLoading(false)
			}
		}
		load()
		return () => { alive = false }
	}, [])

	const q = query.trim().toLowerCase()
	const filtered = useMemo(() => {
		if (!q) return merchants
		return merchants.filter((m) =>
			[m.name, m.slug, m.email].some((v) => (v || '').toLowerCase().includes(q))
		)
	}, [merchants, q])

	// With RLS on and the admin policies missing, the list collapses to the
	// admin's own row. Say so instead of looking like there is one merchant.
	const onlySelfVisible = !loading && !errorMessage
		&& merchants.length === 1 && merchants[0]?.id === merchant?.id

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: isMobile ? L.bg : '#FBFAFE', borderRadius: isMobile ? 0 : '12px',
			overflow: 'hidden', color: L.text, fontFamily: L.font, fontSize: '14px',
		}}>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: isMobile ? '16px 16px 28px' : '22px 26px 30px' }}>
				<Box sx={{
					display: 'flex', alignItems: 'center', justifyContent: 'space-between',
					gap: '16px', mb: '6px', flexWrap: 'wrap',
				}}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						<Box sx={{ ...avatarSx, width: 38, height: 38, borderRadius: '11px' }}>
							<ShieldCheck size={19} strokeWidth={1.8} />
						</Box>
						<Box component="h1" sx={{ fontSize: isMobile ? '20px' : '23px', fontWeight: 800, m: 0, letterSpacing: '-0.02em' }}>
							Admin
						</Box>
						<Box sx={pillSx}>{loading ? '…' : `${merchants.length} merchants`}</Box>
					</Box>
					<Box component="label" sx={{
						display: 'flex', alignItems: 'center', gap: '8px', height: 40,
						width: isMobile ? '100%' : 260, p: '0 13px', borderRadius: '10px',
						bgcolor: '#FFFFFF', border: `1.5px solid ${L.border}`, color: L.muted,
						'&:focus-within': { borderColor: L.primary },
					}}>
						<SvIc name="search" size={15} />
						<Box
							component="input"
							type="text"
							placeholder="Search merchants"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							sx={{
								border: 'none', outline: 'none', bgcolor: 'transparent', fontFamily: 'inherit',
								fontSize: '13.5px', color: L.text, width: '100%',
								'&::placeholder': { color: L.muted },
							}}
						/>
					</Box>
				</Box>
				<Box sx={{ fontSize: '13.5px', fontWeight: 600, color: L.muted, mb: '18px' }}>
					Pick a merchant to open their dashboard and work on their behalf. Everything you do there applies to that merchant.
				</Box>

				{errorMessage && (
					<Alert severity="error" sx={{ mb: '14px', borderRadius: '12px' }} onClose={() => setErrorMessage('')}>
						{errorMessage}
					</Alert>
				)}
				{onlySelfVisible && (
					<Alert severity="warning" sx={{ mb: '14px', borderRadius: '12px' }}>
						Only your own merchant is visible. If row-level security is enabled, make sure
						migrations 39 to 42 (admin policies) have been applied to this database.
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: '40px' }}>
						<CircularProgress size={26} sx={{ color: L.primary }} />
					</Box>
				) : filtered.length === 0 ? (
					<Box sx={{
						p: '44px 16px', textAlign: 'center', color: L.muted, fontWeight: 600,
						fontSize: '13.5px', bgcolor: '#FFFFFF', border: `1px dashed ${L.border}`, borderRadius: '14px',
					}}>
						{q ? <span>No merchants match “{query}”.</span> : <span>No merchants found.</span>}
					</Box>
				) : (
					<Box sx={{ bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '14px', overflow: 'hidden' }}>
						{filtered.map((m, i) => (
							<Box
								component="button"
								type="button"
								key={m.id}
								onClick={() => onSelectMerchant && onSelectMerchant(m)}
								sx={{
									display: 'flex', alignItems: 'center', gap: '12px', width: '100%', textAlign: 'left',
									p: isMobile ? '12px 14px 12px 16px' : '12px 18px', bgcolor: 'transparent', border: 'none',
									borderTop: i === 0 ? 'none' : `1px solid ${L.surface}`, minHeight: 58,
									cursor: 'pointer', fontFamily: 'inherit', color: L.text,
									'&:hover': { bgcolor: '#FBFAFE' },
									'&:active': { bgcolor: L.surface },
								}}
							>
								<Box sx={avatarSx}>{(m.name || '?').trim().charAt(0).toUpperCase()}</Box>
								<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
										<Box component="strong" sx={{ fontSize: '14.5px', fontWeight: 700, letterSpacing: '-0.005em', ...ellipsis }}>
											{m.name || 'Unnamed merchant'}
										</Box>
										{m.id === merchant?.id && <Box sx={pillSx}>you</Box>}
										{m.active === false && (
											<Box sx={{ ...pillSx, bgcolor: '#FBEAEA', color: '#A53B36' }}>booking off</Box>
										)}
									</Box>
									<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted, ...ellipsis }}>
										{[m.slug && `/store/${m.slug}`, m.email].filter(Boolean).join(' · ') || 'No slug or email'}
									</Box>
								</Box>
								<Box sx={{ display: 'flex', color: '#C4BDD6', flex: 'none' }}>
									<SvIc name="chev" size={14} sw={1.8} />
								</Box>
							</Box>
						))}
					</Box>
				)}
			</Box>
		</Box>
	)
}
